import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import LoginScreen from './components/LoginScreen';
import TermsAndConditions from './TermsAndConditions';
import ErrorBoundary from './components/shared/ErrorBoundary';
import OfflineIndicator from './components/shared/OfflineIndicator';
import MaintenanceOverlay from './components/shared/MaintenanceOverlay';
import UpdateBanner from './components/shared/UpdateBanner';
import './i18n';
import i18n from 'i18next';
import api from './services/api';
import { getApiBaseUrl, getAppIdentifier } from './config';
import { saveRememberToken, getRememberToken, clearRememberToken } from './services/authStorage';
import { useDeepLinks } from './hooks/useDeepLinks';
import { useVersionCheck } from './hooks/useVersionCheck';
import useMaintenanceCheck from './hooks/useMaintenanceCheck';

// Lazy-load dashboard and public offer bot
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const PublicOfferBot = lazy(() => import('./components/PublicOfferBot'));

// Parse the public voice-AI offer URL.
// Supports both the clean path `/offer/:offerId` and the legacy
// `/userOfferBot.html?offerId=...` used by the vanilla JS build.
function getPublicOfferId() {
  const { pathname, search } = window.location;
  const m = pathname.match(/^\/offer\/([^/?#]+)\/?$/);
  if (m) return m[1];
  if (pathname === '/userOfferBot.html' || pathname === '/userOfferBot') {
    return new URLSearchParams(search).get('offerId');
  }
  return null;
}

function ExactoApp() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [isAutoLogging, setIsAutoLogging] = useState(true);

  // Public, unauthenticated routes — detected once on first render.
  const isPublicTermsPage = window.location.pathname === '/terms';
  const publicOfferId = getPublicOfferId();
  const isPublicOfferPage = Boolean(publicOfferId);
  const isPublicPage = isPublicTermsPage || isPublicOfferPage;

  // Auto-login from remember token on app start (nidoux pattern)
  useEffect(() => {
    if (isPublicPage) {
      setIsAutoLogging(false);
      return;
    }

    const attemptAutoLogin = async () => {
      const token = getRememberToken();
      if (!token) {
        setIsAutoLogging(false);
        return;
      }

      try {
        const appId = getAppIdentifier();
        const response = await fetch(`${getApiBaseUrl()}/login/token-auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-App-Identifier': appId || ''
          },
          credentials: 'include',
          body: JSON.stringify({ rememberToken: token })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.rememberToken) {
            saveRememberToken(data.rememberToken);
          }
          setIsLoggedIn(true);
          setUserData({
            ...data,
            userType: data.isOwner ? 'owner' : data.isAdmin ? 'admin' : 'user'
          });
          // Sync language on auto-login (fire-and-forget)
          if (typeof api.saveLanguage === 'function') {
            api.saveLanguage(i18n.language || 'en').catch(() => {});
          }
        } else {
          clearRememberToken();
        }
      } catch (err) {
        console.error('Auto-login failed:', err);
        clearRememberToken();
      } finally {
        setIsAutoLogging(false);
      }
    };

    attemptAutoLogin();
  }, [isPublicPage]);

  const handleLoginSuccess = (data) => {
    if (data.rememberToken) {
      saveRememberToken(data.rememberToken);
    }

    setIsLoggedIn(true);
    setUserData({
      ...data,
      userType: data.isOwner ? 'owner' : data.isAdmin ? 'admin' : 'user'
    });
    setShowTerms(false);

    // Sync current language to backend (fire-and-forget)
    if (typeof api.saveLanguage === 'function') {
      api.saveLanguage(i18n.language || 'en').catch(() => {});
    }
  };

  const handleLogout = async () => {
    clearRememberToken();
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggedIn(false);
      setUserData(null);
      setShowTerms(false);
      sessionStorage.clear();
    }
  };

  const handleShowTerms = () => setShowTerms(true);
  const handleBackToLogin = () => setShowTerms(false);
  const handleBackToHome = () => { window.location.href = '/'; };

  const handleDeepLinkNavigation = useCallback((deepLinkData) => {
    if (userData) {
      setUserData(prev => ({
        ...prev,
        deepLinkData
      }));
    }
  }, [userData]);

  // Version check — runs once on native app launch
  const {
    updateAvailable,
    forceUpdate,
    latestVersion,
    updateMessage,
    storeUrl,
    dismissed,
    dismiss,
  } = useVersionCheck({ enabled: isLoggedIn });

  // Maintenance check — polls while logged in
  const {
    isUnderMaintenance,
    message: maintenanceMessage,
    endsAt: maintenanceEndsAt,
  } = useMaintenanceCheck({ enabled: isLoggedIn });

  useDeepLinks((deepLinkData) => {
    handleDeepLinkNavigation(deepLinkData);
  });

  // Suspense fallback spinner
  const dashboardFallback = (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffffff'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e0e0e0',
        borderTopColor: '#10b981',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const renderDashboard = () => {
    const versionProps = {
      updateAvailable: updateAvailable && !dismissed,
      forceUpdate,
      latestVersion,
      updateMessage,
      storeUrl,
      onDismiss: dismiss,
    };
    return (
      <Suspense fallback={dashboardFallback}>
        <AdminDashboard userData={userData} onLogout={handleLogout} versionCheck={versionProps} />
      </Suspense>
    );
  };

  // Public terms page — no login required
  if (isPublicTermsPage) {
    return (
      <div style={{ height: '100vh', width: '100vw' }}>
        <TermsAndConditions onBack={handleBackToHome} />
      </div>
    );
  }

  // Public voice-AI offer page — unauthenticated entry, mirrors the old
  // vanilla `/userOfferBot.html?offerId=…` shortcut.
  if (isPublicOfferPage) {
    return (
      <ErrorBoundary>
        <OfflineIndicator />
        <div style={{ height: '100dvh', width: '100vw', overflow: 'hidden' }}>
          <Suspense fallback={dashboardFallback}>
            <PublicOfferBot offerId={publicOfferId} />
          </Suspense>
        </div>
      </ErrorBoundary>
    );
  }

  if (isAutoLogging) {
    return dashboardFallback;
  }

  return (
    <ErrorBoundary>
      <OfflineIndicator />
      {isUnderMaintenance && (
        <MaintenanceOverlay message={maintenanceMessage} endsAt={maintenanceEndsAt} />
      )}
      {forceUpdate && (
        <UpdateBanner
          updateAvailable={updateAvailable}
          forceUpdate={forceUpdate}
          latestVersion={latestVersion}
          updateMessage={updateMessage}
          storeUrl={storeUrl}
          onDismiss={dismiss}
        />
      )}
      <div style={{ height: '100vh', width: '100vw' }}>
        {!isLoggedIn ? (
          showTerms ? (
            <TermsAndConditions onBack={handleBackToLogin} />
          ) : (
            <LoginScreen
              onLoginSuccess={handleLoginSuccess}
              onShowTerms={handleShowTerms}
            />
          )
        ) : (
          renderDashboard()
        )}
      </div>
    </ErrorBoundary>
  );
}

export default ExactoApp;
